import CollapsibleSettingsPanel from './CollapsibleSettingsPanel'
import InviteDeliveryConfigurationSettings from './InviteDeliveryConfigurationSettings'
import InviteEmailTemplateSettings from './InviteEmailTemplateSettings'
import {
  CAMPAIGN_GROUP_MODES,
  resolveInitialInviteCampaignGroupMode,
  writeStoredInviteCampaignGroupMode
} from 'features/ManageCampaigns/utils/inviteCampaignGroupModes'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'
import { useCallback, useEffect, useState } from 'react'

import styles from './AdvancedInviteSettings.module.scss'

const AdvancedInviteSettings = ({ campaigns, contentSubsidiaryId }) => {
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()
  const [isTemplateExpanded, setIsTemplateExpanded] = useState(false)
  const [isDeliveryExpanded, setIsDeliveryExpanded] = useState(false)
  const [campaignGroupMode, setCampaignGroupMode] = useState(() =>
    resolveInitialInviteCampaignGroupMode(contentSubsidiaryId, campaigns)
  )
  const [mediumOverrides, setMediumOverrides] = useState({})
  const [templates, setTemplates] = useState([])
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)

  useEffect(() => {
    setCampaignGroupMode(resolveInitialInviteCampaignGroupMode(contentSubsidiaryId, campaigns))
  }, [contentSubsidiaryId, campaigns])

  function handleCampaignGroupModeChange(nextMode) {
    setCampaignGroupMode(nextMode)
    writeStoredInviteCampaignGroupMode(contentSubsidiaryId, nextMode)
  }

  const loadTemplates = useCallback(() => {
    setIsLoadingTemplates(true)
    inviteEmailDesignApi
      .listTemplates()
      .then(data => {
        setTemplates(data?.templates || [])
      })
      .catch(() => {
        setTemplates([])
      })
      .finally(() => {
        setIsLoadingTemplates(false)
      })
  }, [])

  useEffect(() => {
    if (!isTemplateExpanded) {
      return
    }

    loadTemplates()
  }, [isTemplateExpanded, loadTemplates])

  function handleToggleTemplateExpanded() {
    if (!isTemplateExpanded) {
      const showedPopup = promptEmailSubscriptionUpgrade({
        onDismiss: () => setIsTemplateExpanded(true)
      })

      if (showedPopup) {
        return
      }
    }

    setIsTemplateExpanded(current => !current)
  }

  function handleToggleDeliveryExpanded() {
    if (!isDeliveryExpanded) {
      const showedPopup = promptEmailSubscriptionUpgrade({
        onDismiss: () => setIsDeliveryExpanded(true)
      })

      if (showedPopup) {
        return
      }
    }

    setIsDeliveryExpanded(current => !current)
  }

  return (
    <section className={styles.section}>
      <CollapsibleSettingsPanel
        title="Delivery Configuration"
        showNewBadge
        isExpanded={isDeliveryExpanded}
        onToggle={handleToggleDeliveryExpanded}
      >
        <InviteDeliveryConfigurationSettings
          campaigns={campaigns}
          contentSubsidiaryId={contentSubsidiaryId}
          campaignGroupMode={campaignGroupMode}
          onCampaignGroupModeChange={handleCampaignGroupModeChange}
          mediumOverrides={mediumOverrides}
          onMediumOverridesChange={setMediumOverrides}
        />
      </CollapsibleSettingsPanel>

      <CollapsibleSettingsPanel
        title="Email Template Configuration"
        showNewBadge
        isExpanded={isTemplateExpanded}
        onToggle={handleToggleTemplateExpanded}
      >
        <InviteEmailTemplateSettings
          templates={templates}
          campaigns={campaigns}
          contentSubsidiaryId={contentSubsidiaryId}
          campaignGroupMode={campaignGroupMode}
          mediumOverrides={mediumOverrides}
          isLoading={isLoadingTemplates}
          onTemplatesChange={loadTemplates}
        />
      </CollapsibleSettingsPanel>
    </section>
  )
}

export default AdvancedInviteSettings
